import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Crown, ArrowUp, ArrowDown, Minus, ChevronLeft, ChevronRight, Sparkles,
  TrendingUp, X, ExternalLink, Loader2, Languages,
} from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/empty";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Dialog } from "@/components/ui/dialog";
import { PriceChart } from "@/components/PriceChart";
import { api, type DesirabilityRankingResp, type DesirabilityRow, type DesirabilityHistoryResp, type DesirabilityCardRow, type DesirabilityLanguagesResp } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 50;

// Sélectionne le nom du Pokémon dans la langue courante.
function speciesNameIn(r: DesirabilityRow, lang: string): string {
  switch (lang) {
    case "FR": return r.french || r.english || "—";
    case "JP": return r.japanese || r.english || "—";
    case "DE": return r.german || r.english || "—";
    case "IT": return r.italian || r.english || "—";
    case "ES": return r.spanish || r.english || "—";
    case "KO": return r.korean || r.english || "—";
    case "CN": return r.simplified_chinese || r.english || "—";
    default:   return r.english || "—";
  }
}

export function Desirability() {
  const { t, fmtMoney, fmtDate, locale } = useI18n();
  const [language, setLanguage] = useState<string>(() => (locale === "fr" ? "FR" : "EN"));
  const [languages, setLanguages] = useState<DesirabilityLanguagesResp["languages"]>([]);
  const [data, setData] = useState<DesirabilityRankingResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedSpecies, setSelectedSpecies] = useState<DesirabilityRow | null>(null);

  // Catalogue de langues disponibles (1 seul fetch)
  useEffect(() => {
    api<DesirabilityLanguagesResp>("/api/desirability/languages")
      .then((r) => setLanguages(r.languages || []))
      .catch(() => setLanguages([]));
  }, []);

  // Reset à la page 1 quand on change de langue
  useEffect(() => {
    setPage(1);
  }, [language]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api<DesirabilityRankingResp>(
        `/api/desirability/ranking?lang=${encodeURIComponent(language)}&limit=${PAGE_SIZE}&offset=${(page - 1) * PAGE_SIZE}`,
      );
      setData(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [page, language]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const top10 = useMemo(() => (page === 1 ? rows.slice(0, 10) : []), [page, rows]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
            <Crown className="h-3.5 w-3.5" />
            {t("desirability.kicker") || "Desirability engine"}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("desirability.title") || "Pokémon desirability ranking"}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-fg">
            {t("desirability.subtitle") ||
              "Average 30-day price per language, across every card of a given species (all forms — Mega / ex / GX / V / VMAX / Star / Cristallino…, tag teams contribute to each species)."}
          </p>
        </div>
        {data?.snapshot_date ? (
          <Badge tone="info" variant="soft">
            Snapshot {data.snapshot_date}
          </Badge>
        ) : null}
      </header>

      <LanguagePicker value={language} onChange={setLanguage} languages={languages} />

      {top10.length > 0 ? <Top10 rows={top10} onPick={setSelectedSpecies} fmtMoney={fmtMoney} language={language} /> : null}

      <Card>
        <CardHeader>
          <div>
            <CardTitle>
              {t("desirability.ranking") || "Full ranking"}{" "}
              <Badge tone="primary" variant="soft" className="ml-1.5 align-middle">{language}</Badge>
            </CardTitle>
            <CardDescription>
              {total
                ? `${total.toLocaleString()} species ranked · page ${page} / ${totalPages}`
                : "—"}
            </CardDescription>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH className="w-12 text-right">#</TH>
                  <TH>{t("desirability.col.species") || "Species"}</TH>
                  {language !== "EN" ? <TH>EN</TH> : null}
                  <TH className="text-right">{t("desirability.col.avg_30d") || "30d avg"} €</TH>
                  <TH className="text-right">{t("desirability.col.cards") || "Cards"}</TH>
                  <TH className="text-right">Δ rank</TH>
                </TR>
              </THead>
              <TBody>
                {loading && rows.length === 0 ? (
                  <SkeletonRows rows={8} cols={language !== "EN" ? 6 : 5} />
                ) : err ? (
                  <tr><td colSpan={6}><Empty title="Failed to load" description={err} /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6}><Empty title={t("desirability.no_data") || "No ranking computed yet"} description={t("desirability.no_data.desc") || "Run scripts/compute-desirability.js."} /></td></tr>
                ) : (
                  rows.map((r) => (
                    <TR key={r.species_id} className="cursor-pointer" onClick={() => setSelectedSpecies(r)}>
                      <TD className="text-right text-xs text-muted-fg tabular-nums">{r.rank}</TD>
                      <TD className="font-medium">{speciesNameIn(r, language)}</TD>
                      {language !== "EN" ? (
                        <TD className="text-muted-fg">{r.english ?? "—"}</TD>
                      ) : null}
                      <TD className="text-right font-mono tabular-nums">{fmtMoney(r.avg_price_eur, "EUR")}</TD>
                      <TD className="text-right text-xs text-muted-fg tabular-nums">{r.n_cards_contributing}</TD>
                      <TD className="text-right"><RankDelta v={r.rank_delta_vs_prev_day} /></TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </CardBody>
        <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-fg">
          <span>{total > 0 ? `${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} / ${total}` : "—"}</span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </Card>

      <SpeciesDetailDialog
        species={selectedSpecies}
        language={language}
        onClose={() => setSelectedSpecies(null)}
        fmtMoney={fmtMoney}
        fmtDate={fmtDate}
      />
    </div>
  );
}

function Top10({
  rows,
  onPick,
  fmtMoney,
  language,
}: {
  rows: DesirabilityRow[];
  onPick: (r: DesirabilityRow) => void;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  language: string;
}) {
  return (
    <div>
      <h2 className="mb-2 text-xs uppercase tracking-wide text-muted-fg">Top 10 · {language}</h2>
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {rows.map((r) => {
          const mainName = speciesNameIn(r, language);
          const altName = language === "EN" ? r.french : r.english;
          return (
            <button
              key={r.species_id}
              type="button"
              onClick={() => onPick(r)}
              className="group text-left"
            >
              <div className={cn(
                "relative overflow-hidden rounded-lg border bg-gradient-to-br from-surface to-surface-2 p-4 shadow-soft transition-shadow hover:shadow-floating",
                r.rank === 1 && "border-warn/60",
                r.rank === 2 && "border-muted-fg/40",
                r.rank === 3 && "border-warn/30",
                r.rank > 3 && "border-border",
              )}>
                <div className="absolute right-2 top-2">
                  <RankDelta v={r.rank_delta_vs_prev_day} compact />
                </div>
                <div className="flex items-baseline gap-1.5">
                  {r.rank === 1 ? <Crown className="h-3.5 w-3.5 text-warn" /> : null}
                  <span className="text-[10px] font-mono uppercase tracking-wide text-muted-fg">#{r.rank}</span>
                </div>
                <div className="mt-1.5 text-sm font-semibold leading-tight">{mainName}</div>
                <div className="text-[11px] text-muted-fg">{altName ?? "—"}</div>
                <div className="mt-3 font-mono text-base font-bold tabular-nums">{fmtMoney(r.avg_price_eur, "EUR")}</div>
                <div className="text-[10px] text-muted-fg">{r.n_cards_contributing} cards · 30d avg</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LanguagePicker({
  value,
  onChange,
  languages,
}: {
  value: string;
  onChange: (v: string) => void;
  languages: DesirabilityLanguagesResp["languages"];
}) {
  // Toujours afficher au moins les "majeures" EN/FR/DE/IT/ES, plus celles
  // qui ont des données. Cap les rares (PT/NL/KO si <5 species) à part.
  const MAJOR = ["EN", "FR", "DE", "IT", "ES", "JP"];
  const visible = MAJOR.filter((l) => languages.some((x) => x.language === l)).concat(
    languages.filter((x) => !MAJOR.includes(x.language) && x.n_species >= 5).map((x) => x.language),
  );
  const fallback = visible.length > 0 ? visible : ["EN"];

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-1.5 shadow-soft">
      <div className="flex items-center gap-1.5 pl-2 pr-1 text-xs text-muted-fg">
        <Languages className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Language</span>
      </div>
      {fallback.map((l) => {
        const n = languages.find((x) => x.language === l)?.n_species ?? 0;
        const active = value === l;
        return (
          <button
            key={l}
            type="button"
            onClick={() => onChange(l)}
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              active ? "bg-surface-2 text-fg shadow-sm" : "text-muted-fg hover:text-fg",
            )}
          >
            <span>{l}</span>
            {n > 0 ? (
              <span className={cn("text-[10px] tabular-nums", active ? "text-muted-fg" : "text-muted-fg/70")}>
                {n}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function RankDelta({ v, compact = false }: { v: number | null; compact?: boolean }) {
  if (v == null) {
    return <span className={cn("text-muted-fg", compact ? "text-[10px]" : "text-xs")}>—</span>;
  }
  if (v === 0) {
    return (
      <span className={cn("inline-flex items-center gap-0.5 text-muted-fg", compact ? "text-[10px]" : "text-xs")}>
        <Minus className="h-2.5 w-2.5" />0
      </span>
    );
  }
  const up = v > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <Badge tone={up ? "success" : "danger"} variant="soft" className={cn("font-mono", compact && "text-[9px] px-1 py-0")}>
      <Icon className="h-2.5 w-2.5" />
      {up ? "+" : ""}{v}
    </Badge>
  );
}

function SpeciesDetailDialog({
  species,
  language,
  onClose,
  fmtMoney,
  fmtDate,
}: {
  species: DesirabilityRow | null;
  language: string;
  onClose: () => void;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | Date | number | null | undefined) => string;
}) {
  const [history, setHistory] = useState<DesirabilityHistoryResp | null>(null);
  const [cards, setCards] = useState<DesirabilityCardRow[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!species) {
      setHistory(null);
      setCards([]);
      return;
    }
    setBusy(true);
    Promise.all([
      api<DesirabilityHistoryResp>(`/api/desirability/species/${species.species_id}/history?lang=${encodeURIComponent(language)}&days=90`),
      api<{ rows: DesirabilityCardRow[] }>(`/api/desirability/species/${species.species_id}/cards?lang=${encodeURIComponent(language)}&limit=15`),
    ])
      .then(([h, c]) => {
        setHistory(h);
        setCards(c.rows || []);
      })
      .catch(() => {
        setHistory(null);
        setCards([]);
      })
      .finally(() => setBusy(false));
  }, [species]);

  const chartData = useMemo(() => {
    if (!history) return [];
    return history.history.map((h) => ({
      t: new Date(h.snapshot_date).getTime(),
      p: Number(h.avg_price_eur),
    }));
  }, [history]);

  return (
    <Dialog
      open={species != null}
      onClose={onClose}
      className="max-w-3xl"
      title={
        species ? (
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span>{speciesNameIn(species, language)}</span>
            {speciesNameIn(species, language) !== species.english ? (
              <span className="text-xs font-normal text-muted-fg">/ {species.english}</span>
            ) : null}
          </div>
        ) : null
      }
      description={
        species ? (
          <span className="flex items-center gap-2">
            <Badge tone="primary" variant="soft">#{species.rank} · {language}</Badge>
            <span className="font-mono">{fmtMoney(species.avg_price_eur, "EUR")} · 30d avg</span>
            <span>· {species.n_cards_contributing} cards</span>
          </span>
        ) : null
      }
      footer={
        <Button variant="outline" size="sm" onClick={onClose}>
          <X className="h-3.5 w-3.5" /> Close
        </Button>
      }
    >
      {busy ? (
        <div className="grid place-items-center py-8 text-muted-fg">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : history && history.history.length > 0 ? (
        <div className="space-y-5">
          <div>
            <div className="mb-2 flex items-center justify-between text-xs text-muted-fg">
              <span className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5" /> History · {history.history.length} snapshots
              </span>
            </div>
            <div className="rounded-md border border-border bg-surface-2/40 p-2">
              <PriceChart data={chartData} height={180} currency="EUR" fmtMoney={fmtMoney} fmtDate={fmtDate} />
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs text-muted-fg">Top cards contributing</div>
            <div className="max-h-72 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="border-b border-border bg-surface-2/40 text-[10px] uppercase tracking-wide text-muted-fg">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Card</th>
                    <th className="px-2 py-1.5 text-left">Set</th>
                    <th className="px-2 py-1.5 text-right">30d avg</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {cards.length === 0 ? (
                    <tr><td colSpan={4} className="px-2 py-3 text-center text-muted-fg">No cards with prices.</td></tr>
                  ) : (
                    cards.map((c) => (
                      <tr key={c.card_id} className="border-b border-border/60 last:border-0">
                        <td className="px-2 py-1.5">
                          <Link to={`/card/${encodeURIComponent(c.card_id)}`} className="font-mono text-primary hover:underline" onClick={onClose}>
                            {c.card_id}
                          </Link>
                          <div className="truncate text-[10px] text-muted-fg">{c.name}</div>
                        </td>
                        <td className="px-2 py-1.5 text-muted-fg">
                          {c.extension ?? c.setcode}
                          {c.is_tag_team_part ? <Badge tone="info" variant="soft" className="ml-1 text-[9px]">Tag team</Badge> : null}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                          {c.average_30d != null ? fmtMoney(c.average_30d, "EUR") : c.average_price != null ? fmtMoney(c.average_price, "EUR") : "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Link to={`/card/${encodeURIComponent(c.card_id)}`} onClick={onClose} className="inline-flex items-center text-muted-fg hover:text-primary">
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-fg">No data.</p>
      )}
    </Dialog>
  );
}
